import React, { useEffect, useState } from 'react';
import { sendWhisper } from '../lib/whisper';

const SERIF_HEAD = "'Merriweather', 'Georgia', 'Times New Roman', serif";
const SERIF_BODY = "'Cormorant Garamond', 'Georgia', 'Times New Roman', serif";
const SANS       = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

const BG_DEEP   = '#050816';
const INK       = '#f4f6fb';
const INK_SOFT  = '#a8b1cb';
const INK_FAINT = '#5a6486';
const PURPLE    = '#a78bfa';
const GOLD      = '#fcd34d';
const RULE      = 'rgba(255,255,255,0.08)';

const FAMILY_KEY      = process.env.REACT_APP_FAMILY_KEY || '';
const UNLOCK_STORAGE  = 'agoracle_family_unlock_v1';
const TOWN_HALL_URL   = process.env.REACT_APP_TOWN_HALL_URL || 'https://discord.gg/3RKGfSsmrt';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readFamilyUnlocked(): boolean {
  if (!FAMILY_KEY) return false;
  try {
    return window.localStorage.getItem(UNLOCK_STORAGE) === FAMILY_KEY;
  } catch {
    return false;
  }
}

export default function Manifesto(): React.JSX.Element {
  // Sticky "Return to the Agora" only appears for already-unlocked sessions
  // on the testing host. Agoracle public domains stay manifesto-only.
  const [showReturn, setShowReturn] = useState(false);
  useEffect(() => {
    setShowReturn(readFamilyUnlocked());
  }, []);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Inter:wght@300;400;500;600&display=swap"
      />

      <style>{`
        .manifesto-prose p { margin: 0; }
        .manifesto-prose em { font-style: italic; }
        .manifesto-link {
          color: ${PURPLE};
          text-decoration: none;
          border-bottom: 1px solid ${PURPLE}55;
          padding-bottom: 1px;
        }
        @media (max-width: 640px) {
          .manifesto-main       { padding: 40px 22px calc(140px + env(safe-area-inset-bottom, 20px)) !important; }
          .manifesto-hook       { padding: 56px 0 36px !important; }
          .manifesto-act        { padding: 44px 0 !important; }
          .manifesto-h1         { font-size: clamp(34px, 9vw, 56px) !important; line-height: 1.08 !important; }
          .manifesto-h2         { font-size: clamp(24px, 6.5vw, 32px) !important; line-height: 1.22 !important; }
          .manifesto-body p     { font-size: 16px !important; line-height: 1.85 !important; }
          .manifesto-header     { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .manifesto-beta       { font-size: 11px !important; padding: 4px 12px !important; letter-spacing: 0.32em !important; }
          .manifesto-waitlist-row { flex-direction: column !important; align-items: stretch !important; }
          .manifesto-waitlist-row > input  { width: 100% !important; }
          .manifesto-waitlist-row > button { width: 100% !important; }
        }
        @keyframes manifesto-beta-pulse {
          0%, 100% { box-shadow: 0 0 0 rgba(252,211,77,0.0); }
          50%      { box-shadow: 0 0 14px rgba(252,211,77,0.55); }
        }
        .manifesto-beta { animation: manifesto-beta-pulse 3s ease-in-out infinite; }
      `}</style>

      <div style={{
        minHeight:       '100vh',
        backgroundColor: BG_DEEP,
        backgroundImage:
          'radial-gradient(ellipse at 20% 10%, rgba(168,85,247,0.08), transparent 55%),' +
          'radial-gradient(ellipse at 85% 90%, rgba(252,211,77,0.05), transparent 55%),' +
          'radial-gradient(ellipse at 50% 50%, rgba(20,30,70,0.4), transparent 70%)',
        color:           INK,
        fontFamily:      SANS,
        WebkitFontSmoothing: 'antialiased',
        overflowX:       'hidden',
      }}>
        <main className="manifesto-main" style={{
          position:  'relative',
          maxWidth:  920,
          margin:    '0 auto',
          padding:   '64px 32px calc(96px + env(safe-area-inset-bottom, 0px))',
          display:   'flex',
          flexDirection: 'column',
        }}>

          {/* ── Brand header ────────────────────────────────────── */}
          <header className="manifesto-header" style={{
            display:        'flex',
            alignItems:     'baseline',
            justifyContent: 'space-between',
            gap:            24,
            paddingBottom:  18,
            borderBottom:   `1px solid ${RULE}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: SERIF_BODY, fontWeight: 500, fontSize: 28, letterSpacing: '0.01em' }}>
                <span style={{ color: INK }}>Agora</span>
                <span style={{ color: PURPLE, fontStyle: 'italic' }}>cle</span>
              </span>
              <span className="manifesto-beta" style={{
                fontFamily:    SANS,
                fontSize:      10,
                fontWeight:    700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color:         '#fde68a',
                padding:       '3px 10px',
                border:        '1px solid #a16207',
                borderRadius:  999,
                background:    'rgba(161,98,7,0.22)',
              }}>
                Beta
              </span>
            </div>
            <div style={{
              fontFamily:    SANS,
              fontSize:      10,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color:         INK_FAINT,
            }}>
              Manifesto · MMXXVI · A Document of Record
            </div>
          </header>

          {/* ── Pilot Phase disclaimer ──────────────────────────── */}
          <aside style={{
            marginTop:     22,
            padding:       '12px 16px',
            border:        `1px solid ${PURPLE}33`,
            borderLeft:    `3px solid ${PURPLE}`,
            borderRadius:  2,
            background:    'rgba(124,58,237,0.06)',
            display:       'flex',
            flexWrap:      'wrap',
            alignItems:    'baseline',
            gap:           12,
          }}>
            <span style={{
              fontFamily:    SANS,
              fontSize:      9,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color:         GOLD,
              fontWeight:    600,
            }}>
              Pilot Phase
            </span>
            <span style={{
              fontFamily: SANS,
              fontSize:   13,
              lineHeight: 1.65,
              color:      INK_SOFT,
              fontWeight: 300,
              flex:       '1 1 240px',
            }}>
              Agoracle is in a closed pilot. Markets, mechanics, and the ledger may
              change without notice while the rite is calibrated. Read the three acts
              below before you enter the square.
            </span>
          </aside>

          {/* ── Hook ────────────────────────────────────────────── */}
          <section className="manifesto-hook" style={{ padding: '72px 0 48px' }}>
            <p style={{
              fontSize:      11,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color:         GOLD,
              margin:        '0 0 22px',
              fontWeight:    500,
              fontFamily:    SANS,
            }}>
              A Manifesto in Three Acts
            </p>
            <h1 className="manifesto-h1" style={{
              fontFamily:    SERIF_HEAD,
              fontWeight:    900,
              fontSize:      'clamp(40px, 6.6vw, 72px)',
              lineHeight:    1.06,
              letterSpacing: '-0.018em',
              color:         INK,
              margin:        '0 0 32px',
              maxWidth:      '18ch',
            }}>
              The <span style={{ color: PURPLE, fontStyle: 'italic', fontWeight: 700 }}>Noise of Hopes.</span><br />
              The Signal of Truth.
            </h1>
            <p className="manifesto-body" style={{
              fontSize:   17,
              lineHeight: 1.78,
              color:      INK_SOFT,
              maxWidth:   '58ch',
              margin:     0,
              fontWeight: 300,
            }}>
              We are building a peer-to-peer prediction marketplace where the wisdom of the
              crowd is staked, contested, and resolved in public. Two ancient instruments — the
              Agora and the Oracle — fused into a single ledger. No house. No edge. Only the gap
              between what people <em style={{ color: INK }}>want</em> to be true and what the
              market <em style={{ color: INK }}>proves</em>.
            </p>
          </section>

          {/* ── Act I · The Agora ─────────────────────────────── */}
          <Act
            number="I"
            kicker="The Agora · Noise of Hopes"
            title="The Marketplace, Without a House"
            body={[
              <>
                The Athenian agora was never merely a market. It was the open square where
                citizens met to argue, to wager, to sell olives and to declare wars. Prices
                whispered through the colonnades alongside rumour. The Agora is, and has always
                been, the <em style={{ color: PURPLE }}>Noise of Hopes</em> — the raw, restless
                aggregate of what the crowd believes the world might become.
              </>,
              <>
                Agoracle inherits that lineage. Every market here is minted peer-to-peer. There
                is <em style={{ color: PURPLE }}>no black-box AI manipulation</em> of the odds,
                no house algorithm tilting the floor in its own favour — only the counterparty
                willing to meet you on the other side of the trade.
              </>,
              <>
                The mechanism we use is what we call{' '}
                <em style={{ color: PURPLE }}>Dynamic Consensus Balancing</em>. As more citizens
                stake their conviction on an outcome, that outcome's share of the price rises in
                proportion; as conviction drains away, the price falls. The number on the screen
                is not a figure a hidden formula has decided is fair. It is the{' '}
                <em style={{ color: INK }}>collective conviction of the ward</em>, expressed as a
                price, in plain view, second by second.
              </>,
              <>
                Through <em style={{ color: PURPLE }}>Social Forking</em>, any participant may
                challenge a market by forking it — proposing a sharper question, a tighter
                resolution, a new lens on the same event. Markets compete in lineage, not
                isolation. The truest framing wins liquidity. Settlement walks the ancestry chain
                and pays tribute to the Root Architect whose original question gave the lineage
                life.
              </>,
            ]}
          />

          {/* ── Act II · The Fortress ─────────────────────────── */}
          <Act
            number="II"
            kicker="The Fortress · Privacy as Architecture"
            title="A Vault Beneath the Square"
            body={[
              <>
                We collect what we must. Nothing more. There are no tracking pixels, no
                behavioural surveillance, no data brokerage, no algorithmic profiling of your
                participation. Your wagers are your own — encrypted in transit, sovereign at
                rest, never sold, never shared.
              </>,
              <>
                Compliance is not a banner; it is a kernel. Every money-moving path runs the
                same gate — verification, self-exclusion, deposit limits — written into the
                core and never short-circuited. The audit ledger is append-only by architecture,
                not by promise. Even we cannot rewrite the past.
              </>,
            ]}
          />

          {/* ── Interlude · The Modern Polis ──────────────────── */}
          <Act
            number="·"
            kicker="The Modern Polis · Norwich"
            title="Thirteen Wards. One Crucible."
            body={[
              <>
                We are not launching in London. We are launching in Norwich — thirteen wards,
                six contesting parties, candidate saturation at a decade high. In most cities an
                election narrows to a two-party tussle; in Norwich 2026 it fractures. Labour,
                Green, Conservative, Liberal Democrat, Reform UK and a long tail of independents
                contest overlapping ground.
              </>,
              <>
                Norwich is, in miniature, a <em style={{ color: PURPLE }}>Modern Polis</em> —
                the closest analogue we have to the city-state from which the Agora was born.
                Thirteen wards become thirteen parallel micro-markets of belief, each with its
                own divergence between doorstep sentiment and headline polling. If the Agoracle
                engine can surface the signal here — where the noise is fragmented and the{' '}
                <em style={{ color: PURPLE }}>Field</em> is crowded with outsiders — it can
                surface it anywhere. Norwich is the stress test by design.
              </>,
            ]}
          />

          {/* ── Act III · The Oracle ──────────────────────────── */}
          <Act
            number="III"
            kicker="The Oracle · Signal of Truth"
            title="Pythia, Dodona, and the Ledger"
            body={[
              <>
                Where the Agora is noise, the Oracle is filter. Antiquity gave us two great
                models for it. At <em style={{ color: GOLD }}>Delphi</em>, the{' '}
                <em style={{ color: GOLD }}>Pythia</em> spoke from a single seat — a centralised
                voice, vapours rising from the rock, prophecy delivered in hexameter. At{' '}
                <em style={{ color: GOLD }}>Dodona</em>, the older sanctuary, priests read the
                rustling of <em style={{ color: GOLD }}>oak leaves</em> — a distributed signal,
                the wind moving through a thousand small witnesses, truth assembled from
                consensus rather than command.
              </>,
              <>
                Agoracle is a Dodona running on a Delphi clock. Every ward, every participant,
                every staked vibe is a leaf in the canopy. The Social Forking Oracle then settles
                each market in two phases — an initial declaration, then a challenge window in
                which any participant may contest the outcome by posting a bond. Truth is not
                handed down; it is converged upon, in public, with every step on the ledger.
              </>,
              <>
                The output of this rite is what we call the{' '}
                <em style={{ color: PURPLE }}>Validated Reality</em>: a price, a winner, a fact
                — sealed by the crowd, witnessed by the ledger, and immune to retrospective
                edit. When a market resolves, a small <em style={{ color: GOLD }}>Agora Levy</em>{' '}
                — a 3% tribute on net winnings — is split between the Root Architect whose
                question opened the inquiry and the commons that sustain the protocol. The
                house keeps nothing. The crowd keeps the rest.
              </>,
              <>
                <span style={{ color: INK, fontWeight: 400 }}>
                  The signal is growing. Leave your mark to be notified when the Agora opens to
                  the public.
                </span>
              </>,
            ]}
          />

          {/* ── Lead Capture · Waitlist + Town Hall ───────────── */}
          <Waitlist />


          {/* ── Footer ────────────────────────────────────────── */}
          <footer style={{
            marginTop:     72,
            paddingTop:    22,
            borderTop:     `1px solid ${RULE}`,
            display:       'flex',
            justifyContent:'space-between',
            alignItems:    'center',
            gap:           16,
            fontFamily:    SANS,
            fontSize:      10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         INK_FAINT,
            flexWrap:      'wrap',
          }}>
            <span>© 2026 Agoracle · Architect: Dromide</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: INK_SOFT }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: PURPLE, boxShadow: `0 0 10px ${PURPLE}`,
                }} />
                The Pulse Is Building
              </span>
              <TownHallButton size="compact" />
            </div>
          </footer>

        </main>

        {showReturn && (
          <a
            href="/"
            style={{
              position:        'fixed',
              right:           20,
              bottom:          'calc(20px + var(--drawer-height, 0px))',
              zIndex:          950,
              display:         'inline-flex',
              alignItems:      'center',
              gap:             10,
              padding:         '11px 18px',
              borderRadius:    999,
              background:      'rgba(124, 58, 237, 0.92)',
              border:          `1px solid ${PURPLE}`,
              color:           '#0b0418',
              fontFamily:      SANS,
              fontSize:        11,
              fontWeight:      600,
              letterSpacing:   '0.22em',
              textTransform:   'uppercase',
              textDecoration:  'none',
              boxShadow:       '0 12px 32px rgba(124,58,237,0.35)',
              backdropFilter:  'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              transition:      'transform 0.18s ease-out, box-shadow 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(124,58,237,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';     e.currentTarget.style.boxShadow = '0 12px 32px rgba(124,58,237,0.35)'; }}
          >
            <span aria-hidden="true" style={{ fontFamily: SERIF_BODY, fontStyle: 'italic', fontSize: 16, lineHeight: 1 }}>↩</span>
            Return to the Agora
          </a>
        )}
      </div>
    </>
  );
}

interface ActProps {
  number: string;
  kicker: string;
  title:  string;
  body:   React.ReactNode[];
}

function Act({ number, kicker, title, body }: ActProps): React.JSX.Element {
  return (
    <section className="manifesto-act" style={{
      padding:    '56px 0',
      borderTop:  `1px solid ${RULE}`,
      display:    'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap:        18,
    }}>
      <div style={{
        display:    'flex',
        alignItems: 'baseline',
        gap:        16,
        flexWrap:   'wrap',
      }}>
        <span style={{
          fontFamily: SERIF_BODY,
          fontStyle:  'italic',
          fontSize:   38,
          color:      PURPLE,
          opacity:    0.7,
          lineHeight: 1,
        }}>
          {number}
        </span>
        <span style={{
          fontFamily:    SANS,
          fontSize:      10,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color:         INK_FAINT,
        }}>
          {kicker}
        </span>
      </div>

      <h2 className="manifesto-h2" style={{
        fontFamily:    SERIF_HEAD,
        fontWeight:    700,
        fontSize:      'clamp(26px, 3.6vw, 38px)',
        lineHeight:    1.22,
        letterSpacing: '-0.012em',
        color:         INK,
        margin:        '4px 0 8px',
        maxWidth:      '24ch',
      }}>
        {title}
      </h2>

      <div className="manifesto-body manifesto-prose" style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: '60ch' }}>
        {body.map((para, i) => (
          <p key={i} style={{
            fontSize:   16,
            lineHeight: 1.82,
            color:      INK_SOFT,
            margin:     0,
            fontWeight: 300,
          }}>
            {para}
          </p>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

type WaitlistStatus = 'idle' | 'sending' | 'sent' | 'invalid' | 'error';

function Waitlist(): React.JSX.Element {
  const [email,  setEmail]  = useState('');
  const [status, setStatus] = useState<WaitlistStatus>('idle');

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) { setStatus('invalid'); return; }
    setStatus('sending');

    const ok = await sendWhisper({
      title:   `[CITIZEN ENROLLMENT] New email for waitlist: ${trimmed}`,
      message: `[CITIZEN ENROLLMENT] New email for waitlist: ${trimmed}`,
      level:   'signal',
      author:  trimmed,
      fields: [
        { name: 'Email',     value: trimmed,                                                                  inline: true },
        { name: 'Source',    value: typeof window !== 'undefined' ? window.location.hostname : 'unknown',     inline: true },
        { name: 'Referrer',  value: typeof document !== 'undefined' ? (document.referrer || '(direct)') : '', inline: false },
      ],
    });

    if (ok) { setStatus('sent'); setEmail(''); }
    else    { setStatus('error'); }
  }

  return (
    <section
      id="waitlist"
      style={{
        marginTop:    72,
        padding:      '40px 32px',
        borderRadius: 4,
        border:       `1px solid ${RULE}`,
        borderTop:    `2px solid ${PURPLE}`,
        background:   'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(8,12,30,0.3) 100%)',
      }}>
      <p style={{
        fontFamily:    SANS,
        fontSize:      11,
        letterSpacing: '0.36em',
        textTransform: 'uppercase',
        color:         GOLD,
        margin:        '0 0 14px',
        fontWeight:    600,
      }}>
        Leave Your Mark
      </p>

      <h3 style={{
        fontFamily:    SERIF_HEAD,
        fontSize:      'clamp(22px, 3.4vw, 32px)',
        fontWeight:    700,
        lineHeight:    1.2,
        letterSpacing: '-0.01em',
        color:         INK,
        margin:        '0 0 10px',
        maxWidth:      '22ch',
      }}>
        Join the Waitlist.
      </h3>

      <p style={{
        fontFamily: SANS,
        fontSize:   15,
        lineHeight: 1.7,
        color:      INK_SOFT,
        margin:     '0 0 22px',
        maxWidth:   '52ch',
        fontWeight: 300,
      }}>
        We will write only twice — once when the Agora opens for the public pilot,
        and once when settlement of the first market is complete. No newsletters,
        no marketing, no third-party sharing.
      </p>

      <form
        onSubmit={submit}
        className="manifesto-waitlist-row"
        style={{
          display:    'flex',
          gap:        10,
          alignItems: 'stretch',
          flexWrap:   'wrap',
        }}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@somewhere.email"
          aria-label="Email address for the Agoracle waitlist"
          value={email}
          onChange={e => { setEmail(e.target.value); if (status !== 'idle' && status !== 'sending') setStatus('idle'); }}
          disabled={status === 'sending' || status === 'sent'}
          style={{
            flex:           '1 1 240px',
            minWidth:       0,
            padding:        '13px 16px',
            background:     '#020410',
            border:         `1px solid ${status === 'invalid' || status === 'error' ? '#f87171' : RULE}`,
            borderRadius:   3,
            color:          INK,
            fontFamily:     SANS,
            fontSize:       15,
            letterSpacing:  '0.02em',
            outline:        'none',
            transition:     'border-color 0.18s',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = PURPLE; }}
          onBlur ={e => { e.currentTarget.style.borderColor = (status === 'invalid' || status === 'error') ? '#f87171' : RULE; }}
        />
        <button
          type="submit"
          disabled={status === 'sending' || status === 'sent'}
          style={{
            padding:        '13px 22px',
            background:     status === 'sent' ? '#166534' : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            border:         `1px solid ${status === 'sent' ? '#22c55e' : PURPLE}`,
            borderRadius:   3,
            color:          status === 'sent' ? '#86efac' : '#faf5ff',
            fontFamily:     SANS,
            fontWeight:     600,
            fontSize:       12,
            letterSpacing:  '0.24em',
            textTransform:  'uppercase',
            cursor:         status === 'sending' || status === 'sent' ? 'default' : 'pointer',
            boxShadow:      status === 'sent' ? 'none' : '0 0 14px rgba(124,58,237,0.35)',
            transition:     'transform 0.15s, filter 0.15s',
          }}
          onMouseEnter={e => { if (status !== 'sending' && status !== 'sent') e.currentTarget.style.filter = 'brightness(1.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}>
          {status === 'sending' ? 'Enrolling…' : status === 'sent' ? '✓ Citizen Enrolled' : 'Join the Waitlist'}
        </button>
      </form>

      {status === 'invalid' && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#fca5a5', fontFamily: SANS }}>
          That email looks off. Try again.
        </p>
      )}
      {status === 'error' && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#fca5a5', fontFamily: SANS }}>
          The Oracle is busy. Please try again.
        </p>
      )}
      {status === 'sent' && (
        <p style={{
          margin:        '14px 0 0',
          fontFamily:    SERIF_BODY,
          fontStyle:     'italic',
          fontSize:      18,
          lineHeight:    1.5,
          color:         '#bbf7d0',
          letterSpacing: '0.01em',
        }}>
          The Oracle has noted your entry. Welcome to the Polis.
        </p>
      )}

      <div style={{
        marginTop:  28,
        paddingTop: 22,
        borderTop:  `1px solid ${RULE}`,
        display:    'flex',
        alignItems: 'center',
        gap:        14,
        flexWrap:   'wrap',
      }}>
        <span style={{
          fontFamily:    SANS,
          fontSize:      11,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color:         INK_FAINT,
          fontWeight:    500,
        }}>
          Or step inside the conversation
        </span>
        <TownHallButton size="primary" />
      </div>
    </section>
  );
}

function TownHallButton({ size }: { size: 'primary' | 'compact' }): React.JSX.Element {
  const isPrimary = size === 'primary';
  const iconSize  = isPrimary ? 18 : 14;
  return (
    <a
      href={TOWN_HALL_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Join the Town Hall on Discord (opens in a new tab)"
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:             isPrimary ? 10 : 8,
        padding:         isPrimary ? '12px 22px' : '8px 16px',
        borderRadius:    999,
        background:      'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
        border:          `1px solid ${PURPLE}`,
        color:           '#faf5ff',
        fontFamily:      SANS,
        fontWeight:      700,
        fontSize:        isPrimary ? 12 : 10,
        letterSpacing:   '0.22em',
        textTransform:   'uppercase',
        textDecoration:  'none',
        boxShadow:       isPrimary
          ? '0 0 18px rgba(124,58,237,0.45)'
          : '0 0 10px rgba(124,58,237,0.32)',
        transition:      'transform 0.15s, box-shadow 0.15s, filter 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = isPrimary
          ? '0 0 26px rgba(167,139,250,0.6)'
          : '0 0 16px rgba(167,139,250,0.45)';
        e.currentTarget.style.filter = 'brightness(1.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isPrimary
          ? '0 0 18px rgba(124,58,237,0.45)'
          : '0 0 10px rgba(124,58,237,0.32)';
        e.currentTarget.style.filter = 'none';
      }}>
      <DiscordGlyph size={iconSize} />
      Join the Town Hall (Discord)
      <ExternalGlyph size={isPrimary ? 11 : 9} />
    </a>
  );
}

function DiscordGlyph({ size }: { size: number }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 -28.5 256 256"
      width={size}
      height={size * (256 / 313)}
      style={{ flexShrink: 0, display: 'block' }}
      fill="currentColor">
      <path d="M216.856 16.597A208.502 208.502 0 0 0 164.042 0c-2.275 4.113-4.933 9.645-6.766 14.046-19.692-2.961-39.203-2.961-58.533 0-1.832-4.4-4.55-9.933-6.846-14.046a207.809 207.809 0 0 0-52.855 16.638C5.618 67.147-3.443 116.4 1.087 164.956c22.169 16.555 43.653 26.612 64.775 33.193A161.094 161.094 0 0 0 79.735 175.3a136.413 136.413 0 0 1-21.846-10.632 108.636 108.636 0 0 0 5.355-4.237c42.122 19.702 87.89 19.702 129.51 0a131.66 131.66 0 0 0 5.355 4.237 136.07 136.07 0 0 1-21.886 10.653c4.006 8.02 8.638 15.67 13.873 22.848 21.142-6.58 42.646-16.637 64.815-33.213 5.316-56.288-9.08-105.09-38.056-148.36ZM85.474 135.095c-12.645 0-23.015-11.805-23.015-26.18s10.149-26.2 23.015-26.2c12.867 0 23.236 11.804 23.015 26.2.02 14.375-10.148 26.18-23.015 26.18Zm85.051 0c-12.645 0-23.014-11.805-23.014-26.18s10.148-26.2 23.014-26.2c12.867 0 23.236 11.804 23.015 26.2 0 14.375-10.148 26.18-23.015 26.18Z" />
    </svg>
  );
}

function ExternalGlyph({ size }: { size: number }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      style={{ flexShrink: 0, opacity: 0.85, marginLeft: 2 }}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  );
}
